import { neon } from '@neondatabase/serverless'

// Get connection string from environment (Vite only exposes VITE_ prefixed vars to client)
const connectionString = import.meta.env.VITE_NEON_DATABASE_URL

if (!connectionString) {
  console.warn('Warning: VITE_NEON_DATABASE_URL not set. Database operations will fail.')
  console.warn('Please set VITE_NEON_DATABASE_URL in your .env.local file')
}

// Initialize Neon client (works in browser with @neondatabase/serverless)
let sql = null
if (connectionString) {
  try {
    sql = neon(connectionString)
  } catch (error) {
    console.error('Failed to initialize Neon client:', error)
  }
}

// Helper function to create a signature session
export async function createSignatureSession(data) {
  if (!sql) {
    throw new Error('Database not initialized. Please set VITE_NEON_DATABASE_URL in your environment variables.')
  }
  
  const { name, profession, style, status = 'submitted' } = data
  
  const result = await sql`
    INSERT INTO signature_sessions (name, profession, style, status)
    VALUES (${name}, ${profession}, ${style}, ${status})
    RETURNING *
  `
  
  return result[0]
}

// Helper function to update a signature session
export async function updateSignatureSession(sessionId, updates) {
  if (!sql) {
    throw new Error('Database not initialized. Please set VITE_NEON_DATABASE_URL in your environment variables.')
  }
  
  // Only allow known column names to prevent SQL injection
  const allowedColumns = ['status', 'preview_urls', 'selected_url', 'final_jpg_url', 'final_png_url', 'final_svg_url', 'stripe_session_id', 'payment_status', 'customer_email']
  
  // Filter updates to only allowed columns
  const validUpdates = {}
  Object.entries(updates).forEach(([key, value]) => {
    if (allowedColumns.includes(key)) {
      validUpdates[key] = value
    }
  })
  
  if (Object.keys(validUpdates).length === 0) {
    throw new Error('No valid fields to update')
  }
  
  // Update each field individually using template literals (Neon's API requirement)
  // This is safe because column names are whitelisted
  if (validUpdates.status !== undefined) {
    await sql`UPDATE signature_sessions SET status = ${validUpdates.status} WHERE id = ${sessionId}`
  }
  if (validUpdates.preview_urls !== undefined) {
    await sql`UPDATE signature_sessions SET preview_urls = ${validUpdates.preview_urls} WHERE id = ${sessionId}`
  }
  if (validUpdates.selected_url !== undefined) {
    await sql`UPDATE signature_sessions SET selected_url = ${validUpdates.selected_url} WHERE id = ${sessionId}`
  }
  if (validUpdates.final_jpg_url !== undefined) {
    await sql`UPDATE signature_sessions SET final_jpg_url = ${validUpdates.final_jpg_url} WHERE id = ${sessionId}`
  }
  if (validUpdates.final_png_url !== undefined) {
    await sql`UPDATE signature_sessions SET final_png_url = ${validUpdates.final_png_url} WHERE id = ${sessionId}`
  }
  if (validUpdates.final_svg_url !== undefined) {
    await sql`UPDATE signature_sessions SET final_svg_url = ${validUpdates.final_svg_url} WHERE id = ${sessionId}`
  }
  if (validUpdates.stripe_session_id !== undefined) {
    await sql`UPDATE signature_sessions SET stripe_session_id = ${validUpdates.stripe_session_id} WHERE id = ${sessionId}`
  }
  if (validUpdates.payment_status !== undefined) {
    await sql`UPDATE signature_sessions SET payment_status = ${validUpdates.payment_status} WHERE id = ${sessionId}`
  }
  if (validUpdates.customer_email !== undefined) {
    await sql`UPDATE signature_sessions SET customer_email = ${validUpdates.customer_email} WHERE id = ${sessionId}`
  }
  
  // Return the updated record
  const result = await sql`
    SELECT * FROM signature_sessions
    WHERE id = ${sessionId}
    LIMIT 1
  `
  
  return result[0]
}

// Helper function to get a signature session
export async function getSignatureSession(sessionId) {
  if (!sql) {
    throw new Error('Database not initialized. Please set VITE_NEON_DATABASE_URL in your environment variables.')
  }
  
  const result = await sql`
    SELECT * FROM signature_sessions
    WHERE id = ${sessionId}
    LIMIT 1
  `
  
  return result[0]
}

// Export sql for direct queries if needed
export { sql }
